import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config';
import { Player } from '../../types';

interface JoinOptionsModalProps {
  isOpen: boolean;
  isSubstitute: boolean;
  onClose: () => void;
  onSubmit: (formData: { name: string; position: string; rating: number; isSubstitute: boolean }) => void;
  onReplacePlayer: (playerId: string, formData: { name: string; position: string; rating: number; isSubstitute: boolean }) => void;
  isLoading: boolean;
  matchId: string;
  players: Player[];
  substitutes: Player[];
  maxPlayers?: number; // Agregar prop para el máximo de jugadores por equipo
  onError?: (error: string) => void; // Callback para manejar errores
}

export const JoinOptionsModal: React.FC<JoinOptionsModalProps> = ({
  isOpen,
  isSubstitute,
  onClose,
  onSubmit,
  onReplacePlayer,
  isLoading,
  matchId,
  players,
  substitutes,
  maxPlayers = 5 // Default a 5 jugadores por equipo
}) => {
  const { user } = useAuth();
  const [selectedOption, setSelectedOption] = useState<'new' | 'replace' | null>(null);
  const [selectedPlayerToReplace, setSelectedPlayerToReplace] = useState<string | null>(null);
  
  // Calcular si el partido está lleno para jugadores principales
  const isMatchFull = players.length >= (maxPlayers * 2); // 2 equipos
  
  // Forzar isSubstitute si el partido está lleno
  const forcedIsSubstitute = isMatchFull || isSubstitute;
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    position: user?.position || 'Mediocampista',
    rating: user?.rating || 3,
    isSubstitute: forcedIsSubstitute // Usar el valor forzado
  });

  // Filtrar jugadores invitados (sin userId)
  // Cuando el partido está lleno pero hay invitados, permitir reemplazar invitados de cualquier tipo
  const allPlayers = [...players, ...substitutes];
  const invitedPlayers = allPlayers.filter(p => !p.userId);
  
  // Solo usar relevantPlayers para determinar el tipo de unión (principal vs suplente)
  const relevantPlayers = forcedIsSubstitute ? substitutes : players;
  
  // Verificar si hay jugadores invitados con el mismo nombre que el usuario
  const duplicateNameInvitedPlayers = invitedPlayers.filter(p => 
    p.name.toLowerCase() === (user?.name || '').toLowerCase()
  );
  
  // Si hay jugadores invitados con el mismo nombre, sugerir reemplazarlos
  const hasDuplicateNameInvited = duplicateNameInvitedPlayers.length > 0;

  // Actualizar formulario cuando el usuario cambie o cuando cambie el estado del partido
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        position: user.position || 'Mediocampista',
        rating: user.rating || 3,
        isSubstitute: forcedIsSubstitute // Siempre usar el valor forzado
      });
    }
  }, [user, forcedIsSubstitute]); // Usar forcedIsSubstitute en lugar de isSubstitute

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedOption === 'replace' && selectedPlayerToReplace) {
      onReplacePlayer(selectedPlayerToReplace, formData);
    } else {
      onSubmit(formData);
    }
  };

  const handleJoinAsNewPlayer = () => {
    // Usar automáticamente la información del usuario autenticado
    const userFormData = {
      name: user?.name || '',
      position: user?.position || 'Mediocampista',
      rating: user?.rating || 3,
      isSubstitute: forcedIsSubstitute // Usar el valor forzado
    };
    onSubmit(userFormData);
  };

  const handleClose = () => {
    setSelectedOption(null);
    setSelectedPlayerToReplace(null);
    setFormData({
      name: user?.name || '',
      position: user?.position || 'Mediocampista',
      rating: user?.rating || 3,
      isSubstitute: forcedIsSubstitute // Usar el valor forzado
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: '18px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        border: '1px solid #33383B'
      }}>
        <h3 style={{ 
          color: '#00E676',
          marginBottom: '20px',
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          letterSpacing: '0.08em'
        }}>
          {forcedIsSubstitute ? 'Unirse como Suplente' : 'Unirse al Partido'}
        </h3>

        {/* Mostrar mensaje si el partido está lleno */}
        {isMatchFull && (
          <div style={{
            backgroundColor: '#FF9800',
            color: '#181A1B',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📋 El partido está lleno. Te podés anotar en la lista de espera y te avisamos cuando se libere un lugar.
          </div>
        )}

        {/* Mostrar mensaje si hay jugadores invitados disponibles para reemplazar */}
        {invitedPlayers.length > 0 && (
          <div style={{
            backgroundColor: '#00E676',
            color: '#181A1B',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            ℹ️ Hay {invitedPlayers.length} jugador{invitedPlayers.length > 1 ? 'es' : ''} invitado{invitedPlayers.length > 1 ? 's' : ''} que puedes reemplazar.
            {hasDuplicateNameInvited && ' También hay jugadores con tu nombre.'}
          </div>
        )}

        {!selectedOption ? (
          // Pantalla de selección de opción
          <div>
            <p style={{ 
              color: '#FFFFFF', 
              marginBottom: '20px',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              {isMatchFull 
                ? '¿Cómo te gustaría unirte como suplente?'
                : '¿Cómo te gustaría unirte al partido?'
              }
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={handleJoinAsNewPlayer}
                style={{
                  padding: '16px',
                  backgroundColor: '#2C2F33',
                  color: '#FFFFFF',
                  border: '2px solid #00E676',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left'
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {forcedIsSubstitute ? '🔄 Unirse como suplente' : '➕ Unirse como nuevo jugador'}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  {forcedIsSubstitute 
                    ? 'Te unirás como suplente con tu cuenta de usuario'
                    : `Te unirás al partido con tu cuenta de usuario (${user?.name})`
                  }
                </div>
                {isMatchFull && (
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#FF9800', 
                    marginTop: '4px',
                    fontStyle: 'italic'
                  }}>
                    ⚠️ Opción forzada - partido lleno
                  </div>
                )}
                {hasDuplicateNameInvited && (
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#00E676', 
                    marginTop: '4px',
                    fontStyle: 'italic'
                  }}>
                    ℹ️ Se permitirá el nombre duplicado (usuario autenticado)
                  </div>
                )}
              </button>

              {invitedPlayers.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedOption('replace')}
                  style={{
                    padding: '16px',
                    backgroundColor: '#2C2F33',
                    color: '#FFFFFF',
                    border: '1px solid #FF9800',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>🔄 Reemplazar jugador invitado</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>
                    Reemplazarás uno de los {invitedPlayers.length} jugador{invitedPlayers.length > 1 ? 'es' : ''} invitado{invitedPlayers.length > 1 ? 's' : ''} con tu cuenta
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#00E676', 
                    marginTop: '4px',
                    fontStyle: 'italic'
                  }}>
                    ✅ Disponible incluso si el partido está lleno
                  </div>
                </button>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleClose}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#33383B',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : selectedOption === 'replace' && !selectedPlayerToReplace ? (
          // Pantalla de selección de jugador a reemplazar
          <div>
            <p style={{ 
              color: '#FFFFFF', 
              marginBottom: '20px',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              Selecciona el jugador invitado que quieres reemplazar:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {invitedPlayers.map(player => (
                <button
                  key={player.id}
                  type="button"
                  onClick={() => {
                    // Determinar si el jugador a reemplazar es suplente o principal
                    const isReplacedPlayerSubstitute = substitutes.some(sub => sub.id === player.id);
                    
                    // Automáticamente usar los datos del usuario para reemplazar
                    const userFormData = {
                      name: user?.name || '',
                      position: user?.position || 'Mediocampista',
                      rating: user?.rating || 3,
                      isSubstitute: isReplacedPlayerSubstitute // Mantener el mismo tipo que el jugador reemplazado
                    };
                    onReplacePlayer(player.id, userFormData);
                  }}
                  style={{
                    padding: '12px',
                    backgroundColor: '#2C2F33',
                    color: '#FFFFFF',
                    border: '1px solid #FF9800',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>{player.name}</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>
                    {player.position} • {'★'.repeat(player.rating)}
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>
                    Reemplazar con: {user?.name} ({user?.position || 'Mediocampista'}, {'★'.repeat(user?.rating || 3)})
                    {substitutes.some(sub => sub.id === player.id) ? ' (como suplente)' : ' (como jugador principal)'}
                  </div>
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleClose}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#33383B',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          // Formulario de datos del jugador
          <form onSubmit={handleSubmit}>
            {selectedOption === 'replace' && selectedPlayerToReplace && (
              <div style={{ 
                backgroundColor: '#FF9800', 
                color: '#181A1B', 
                padding: '12px', 
                borderRadius: '8px', 
                marginBottom: '16px',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                🔄 Reemplazando: {invitedPlayers.find(p => p.id === selectedPlayerToReplace)?.name}
                {substitutes.some(sub => sub.id === selectedPlayerToReplace) ? ' (suplente)' : ' (jugador principal)'}
              </div>
            )}

            {/* Mostrar que se unirá como suplente si el partido está lleno */}
            {isMatchFull && (
              <div style={{
                backgroundColor: '#00E676',
                color: '#181A1B',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                ✅ Te unirás como suplente (partido lleno)
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="join-options-name" style={{
                display: 'block',
                marginBottom: '8px',
                color: '#FFFFFF',
                fontWeight: '600'
              }}>
                Tu nombre:
              </label>
              <input
                id="join-options-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Tu nombre"
                required
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid #33383B',
                  backgroundColor: '#181A1B',
                  color: '#FFFFFF',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="join-options-position" style={{
                display: 'block',
                marginBottom: '8px',
                color: '#FFFFFF',
                fontWeight: '600'
              }}>
                Posición:
              </label>
              <select
                id="join-options-position"
                value={formData.position}
                onChange={(e) => setFormData({...formData, position: e.target.value})}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid #33383B',
                  backgroundColor: '#181A1B',
                  color: '#FFFFFF',
                  fontSize: '16px'
                }}
              >
                <option value="Arquero">🥅 Arquero</option>
                <option value="Defensor">🛡️ Defensor</option>
                <option value="Mediocampista">⚽ Mediocampista</option>
                <option value="Delantero">🚀 Delantero</option>
              </select>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <p id="join-options-rating-label" style={{
                display: 'block',
                marginBottom: '8px',
                color: '#FFFFFF',
                fontWeight: '600',
                margin: '0 0 8px 0'
              }}>
                Nivel (1-5 estrellas):
              </p>
              <div style={{ display: 'flex', gap: '8px' }} role="group" aria-labelledby="join-options-rating-label">
                {[1, 2, 3, 4, 5].map(rating => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setFormData({...formData, rating})}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: formData.rating >= rating ? '#FFD600' : '#33383B',
                      color: formData.rating >= rating ? '#181A1B' : '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  if (selectedOption === 'replace') {
                    setSelectedPlayerToReplace(null);
                  } else {
                    setSelectedOption(null);
                  }
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#33383B',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Atrás
              </button>
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: isLoading ? '#6C757D' : '#00E676',
                  color: isLoading ? '#FFFFFF' : '#181A1B',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {isLoading ? 'Uniéndose...' : selectedOption === 'replace' ? 'Reemplazar' : 'Unirse'}
                {forcedIsSubstitute && ' como suplente'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}; 